import React from 'react';
import {OneRepChartPoint} from 'components/dashboard/manager/nine-box/one-rep-chart/OneRepData.utils';
import {scales, svgDimensions} from 'components/dashboard/manager/nine-box/NineBox.const';
import {line as d3line} from 'd3-shape';
import {theme} from 'charts/components/style';
import {Text} from '@visx/text';
import clamp from 'lodash/clamp';

const COLOR = '#2879FF';
const RADIUS = 20;
const {xScale, yScale} = scales;
const clampWidth = x => clamp(x, 0, svgDimensions.width);
const clampHeight = x => clamp(x, 0, svgDimensions.height);

const leftX = d3line()
  .x(d => clampWidth(xScale(d[0])! - RADIUS))
  .y(d => yScale(d[1])!);
const rightX = d3line()
  .x(d => clampWidth(xScale(d[0])! + RADIUS))
  .y(d => yScale(d[1])!);
const lowerY = d3line()
  .x(d => xScale(d[0])!)
  .y(d => clampHeight(yScale(d[1])! - RADIUS));
const upperY = d3line()
  .x(d => xScale(d[0])!)
  .y(d => clampHeight(yScale(d[1])! + RADIUS));

function Line({d}) {
  return (
    <path
      d={d}
      style={{
        stroke: COLOR,
        strokeWidth: 1,
        strokeLinecap: 'round',
        fill: 'none',
      }}
    />
  );
}

function XGapLine({x, y}) {
  return (
    <>
      <Line
        d={leftX([
          [x, y],
          [0, y],
        ])}
      />
      <Line
        d={rightX([
          [x, y],
          [100, y],
        ])}
      />
    </>
  );
}

function YGapLine({x, y}) {
  return (
    <>
      <Line
        d={upperY([
          [x, 0],
          [x, y],
        ])}
      />
      <Line
        d={lowerY([
          [x, 100],
          [x, y],
        ])}
      />
    </>
  );
}

interface CrosshairsProps {
  data: Omit<OneRepChartPoint, 'date'>;
}

export function Crosshairs({data}: CrosshairsProps) {
  const {x, y, xValueStr, yValueStr} = data;

  return (
    <>
      <XGapLine x={x} y={y} />
      <YGapLine x={x} y={y} />
      <circle cx={xScale(x)} cy={yScale(y)} r={RADIUS} fill="none" stroke={COLOR} strokeWidth={1} />
      <CrosshairText {...textPos(x, y, 'x')} text={xValueStr} />
      <CrosshairText {...textPos(x, y, 'y')} text={yValueStr} />
    </>
  );
}

function textPos(x, y, axis): Omit<CrosshairTextProps, 'text'> {
  if (axis === 'x') {
    return {
      x: x < 50 ? xScale(x + 2)! : xScale(x - 2)!,
      y: y < 50 ? yScale(98)! : yScale(2)!,
      textAnchor: x < 50 ? 'start' : 'end',
      verticalAnchor: y < 50 ? 'start' : 'end',
    };
  } else {
    return {
      x: x < 50 ? xScale(98)! : xScale(2)!,
      y: y < 50 ? yScale(y + 2)! : yScale(y - 2)!,
      textAnchor: x < 50 ? 'end' : 'start',
      verticalAnchor: y < 50 ? 'end' : 'start',
    };
  }
}

interface CrosshairTextProps {
  x: number;
  y: number;
  text: string;
  verticalAnchor?: 'start' | 'end';
  textAnchor?: 'start' | 'end';
}

function CrosshairText({x, y, text, verticalAnchor, textAnchor}: CrosshairTextProps) {
  return (
    <Text
      x={x}
      y={y}
      width={200}
      verticalAnchor={verticalAnchor}
      textAnchor={textAnchor}
      fontFamily={theme.fonts.primaryFontFamily}
      fontSize={12}
      style={{
        strokeLinejoin: 'round',
        // @ts-ignore This is a valid value for fontWeight
        fontWeight: '800',
      }}
      fill={'#889db9'}
      stroke={theme.colors.chartTextInvertedPrimary}
      strokeWidth={3}
      paintOrder={'stroke'}>
      {text}
    </Text>
  );
}
